/**
 * Tabs Component
 *
 * Componente de abas baseado em @radix-ui/react-tabs com padrão shadcn/ui.
 * Suporta:
 *   - API items-based: <Tabs items={tabItems} />
 *   - Compound shadcn: <Tabs><TabsList><TabsTrigger /><TabsContent />
 *   - Compound legado: <TabsProvider><TabList><Tab /><TabPanel />
 *
 * @since 2025-12-16
 * @updated 2026-04-06 — migrado para shadcn/ui (Radix Tabs + cn())
 * @epic INIT-0046 Epic 0002
 */

import * as React from 'react';
import * as RadixTabs from '@radix-ui/react-tabs';
import { useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/utils';

// ==========================================
// TYPES
// ==========================================

export interface TabItem {
  key: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  badge?: string | number;
  disabled?: boolean;
  content: React.ReactNode;
}

type TabVariant = 'line' | 'pills' | 'boxed';
type TabSize = 'sm' | 'md' | 'lg';

// ==========================================
// SHADCN/UI COMPOUND COMPONENTS
// ==========================================

/**
 * Tabs root — wrapper de @radix-ui/react-tabs Root.
 * Suporta controlled (value+onValueChange) e uncontrolled (defaultValue).
 */
const TabsRoot = React.forwardRef<
  React.ElementRef<typeof RadixTabs.Root>,
  React.ComponentPropsWithoutRef<typeof RadixTabs.Root>
>(({ className, ...props }, ref) => (
  <RadixTabs.Root ref={ref} className={cn('w-full', className)} {...props} />
));
TabsRoot.displayName = RadixTabs.Root.displayName;

/**
 * TabsList — barra de abas no padrão shadcn/ui.
 */
const TabsList = React.forwardRef<
  React.ElementRef<typeof RadixTabs.List>,
  React.ComponentPropsWithoutRef<typeof RadixTabs.List>
>(({ className, ...props }, ref) => (
  <RadixTabs.List
    ref={ref}
    className={cn(
      'inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground',
      className
    )}
    {...props}
  />
));
TabsList.displayName = RadixTabs.List.displayName;

/**
 * TabsTrigger — botão de aba no padrão shadcn/ui.
 */
const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof RadixTabs.Trigger>,
  React.ComponentPropsWithoutRef<typeof RadixTabs.Trigger>
>(({ className, ...props }, ref) => (
  <RadixTabs.Trigger
    ref={ref}
    className={cn(
      'inline-flex items-center justify-center whitespace-nowrap rounded-lg px-3 py-1 text-sm font-medium ring-offset-background transition-all',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      'disabled:pointer-events-none disabled:opacity-50',
      'data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow',
      className
    )}
    {...props}
  />
));
TabsTrigger.displayName = RadixTabs.Trigger.displayName;

/**
 * TabsContent — painel de conteúdo shadcn/ui.
 */
const TabsContent = React.forwardRef<
  React.ElementRef<typeof RadixTabs.Content>,
  React.ComponentPropsWithoutRef<typeof RadixTabs.Content>
>(({ className, ...props }, ref) => (
  <RadixTabs.Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = RadixTabs.Content.displayName;

// ==========================================
// LEGADO — ALIAS PARA COMPOUND COMPONENTS
//
// TabList  → TabsList
// Tab      → TabsTrigger  (suporta prop `value` ou `tabKey`)
// TabPanel → TabsContent  (suporta prop `value` ou `tabKey`)
// ==========================================

/**
 * TabList — alias de TabsList para compatibilidade com código legado.
 */
const TabList = React.forwardRef<
  React.ElementRef<typeof RadixTabs.List>,
  React.ComponentPropsWithoutRef<typeof RadixTabs.List> & {
    /** @deprecated use className para estilizar */
    variant?: TabVariant;
  }
>(({ variant: _variant, className, ...props }, ref) => (
  <TabsList
    ref={ref}
    className={cn('w-auto', className)}
    {...props}
  />
));
TabList.displayName = 'TabList';

interface TabProps extends Omit<React.ComponentPropsWithoutRef<typeof RadixTabs.Trigger>, 'value'> {
  /** Chave da aba — equivalente a `value` no Radix. */
  value?: string;
  /** @deprecated use `value` */
  tabKey?: string;
  icon?: React.ReactNode;
  badge?: string | number;
}

/**
 * Tab — alias de TabsTrigger com suporte a ícone e badge.
 * Suporta prop `value` (shadcn) e `tabKey` (legado).
 */
const Tab = React.forwardRef<React.ElementRef<typeof RadixTabs.Trigger>, TabProps>(
  ({ value, tabKey, icon, badge, children, className, ...props }, ref) => {
    const resolvedValue = value ?? tabKey ?? '';
    return (
      <TabsTrigger ref={ref} value={resolvedValue} className={cn('gap-2', className)} {...props}>
        {icon && <span className="h-4 w-4 shrink-0">{icon}</span>}
        <span>{children}</span>
        {badge !== undefined && (
          <span className="rounded-full bg-muted-foreground/20 px-1.5 py-0.5 text-xs">
            {badge}
          </span>
        )}
      </TabsTrigger>
    );
  }
);
Tab.displayName = 'Tab';

interface TabPanelProps extends Omit<React.ComponentPropsWithoutRef<typeof RadixTabs.Content>, 'value'> {
  /** Chave da aba — equivalente a `value` no Radix. */
  value?: string;
  /** @deprecated use `value` */
  tabKey?: string;
}

/**
 * TabPanel — alias de TabsContent.
 * Suporta prop `value` (shadcn) e `tabKey` (legado).
 */
const TabPanel = React.forwardRef<React.ElementRef<typeof RadixTabs.Content>, TabPanelProps>(
  ({ value, tabKey, className, ...props }, ref) => {
    const resolvedValue = value ?? tabKey ?? '';
    return (
      <TabsContent
        ref={ref}
        value={resolvedValue}
        className={cn('mt-4', className)}
        {...props}
      />
    );
  }
);
TabPanel.displayName = 'TabPanel';

// ==========================================
// URL SYNC HOOK
// ==========================================

/**
 * Hook para sincronizar o estado de uma aba com a URL (query string).
 * Pode ser usado diretamente por páginas com compound Tabs.
 *
 * @example
 *   const [activeTab, setActiveTab] = useTabsUrl('tab', 'overview');
 *   <Tabs value={activeTab} onValueChange={setActiveTab}>...</Tabs>
 */
function useTabsUrl(
  urlKey: string,
  defaultValue: string,
): [string, (value: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentValue = searchParams.get(urlKey) ?? defaultValue;

  const setValue = React.useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === defaultValue) {
            next.delete(urlKey);
          } else {
            next.set(urlKey, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [urlKey, defaultValue, setSearchParams],
  );

  return [currentValue, setValue];
}

// ==========================================
// STYLES — para Tabs items-based (variant/size)
// ==========================================

const variantListStyles: Record<TabVariant, string> = {
  line: 'border-b border-border rounded-none bg-transparent p-0 h-auto justify-start',
  pills: 'gap-2 rounded-none bg-transparent p-0 h-auto',
  boxed: 'bg-muted p-1 rounded-lg gap-1 h-auto',
};

const variantTriggerStyles: Record<TabVariant, { active: string; inactive: string; base: string }> = {
  line: {
    base: 'relative pb-3 px-4 font-medium transition-colors rounded-none data-[state=active]:shadow-none',
    active:
      'text-[var(--brand-primary)] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-[var(--brand-primary)] data-[state=active]:bg-transparent',
    inactive: 'text-muted-foreground hover:text-foreground',
  },
  pills: {
    base: 'px-4 py-2 rounded-full font-medium transition-colors data-[state=active]:shadow-none',
    active:
      'data-[state=active]:bg-[var(--brand-primary)] data-[state=active]:text-white',
    inactive: 'bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground',
  },
  boxed: {
    base: 'px-4 py-2 rounded-lg font-medium transition-colors',
    active: 'data-[state=active]:bg-background data-[state=active]:text-foreground',
    inactive: 'text-muted-foreground hover:text-foreground',
  },
};

const sizeStyles: Record<TabSize, string> = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
};

// ==========================================
// MAIN Tabs COMPONENT — polimórfico
//
// Suporta duas APIs:
//   1. items-based:  <Tabs items={...} />
//   2. compound:     <Tabs defaultValue="..."><TabList>...</TabList><TabPanel .../>
// ==========================================

/** Props comuns para URL sync */
interface TabsUrlProps {
  /** Sincroniza a aba ativa com query string da URL. */
  syncWithUrl?: boolean;
  /** Chave na query string (default: 'tab'). */
  urlKey?: string;
}

/** Props para a API items-based */
interface TabsItemsProps extends TabsUrlProps {
  items: TabItem[];
  defaultActiveKey?: string;
  activeKey?: string;
  onChange?: (key: string) => void;
  variant?: TabVariant;
  size?: TabSize;
  fullWidth?: boolean;
  className?: string;
  children?: never;
}

/** Props para a API compound (forwarded ao Radix Root) */
type TabsCompoundProps = Omit<React.ComponentPropsWithoutRef<typeof RadixTabs.Root>, 'children'> & TabsUrlProps & {
  items?: never;
  children: React.ReactNode;
};

type TabsProps = TabsItemsProps | TabsCompoundProps;

/**
 * Tabs — componente polimórfico de abas.
 *
 * Modo items-based (compatibilidade):
 *   <Tabs items={tabItems} defaultActiveKey="key1" />
 *
 * Modo compound (shadcn/Radix):
 *   <Tabs defaultValue="key1">
 *     <TabList><Tab value="key1">Label</Tab></TabList>
 *     <TabPanel value="key1">Content</TabPanel>
 *   </Tabs>
 */
function Tabs(props: TabsProps) {
  // Modo compound — delega ao Radix Root diretamente
  if (!('items' in props) || props.items === undefined) {
    const { className, children, syncWithUrl, urlKey = 'tab', ...rest } = props as TabsCompoundProps;

    if (syncWithUrl) {
      return (
        <TabsUrlSync urlKey={urlKey} defaultValue={rest.defaultValue ?? ''} onChange={rest.onValueChange} className={className}>
          {children}
        </TabsUrlSync>
      );
    }

    return (
      <RadixTabs.Root className={cn('w-full', className)} {...rest}>
        {children}
      </RadixTabs.Root>
    );
  }

  // Modo items-based
  const {
    items,
    defaultActiveKey,
    activeKey: controlledActiveKey,
    onChange,
    variant = 'line',
    size = 'md',
    fullWidth = false,
    className,
    syncWithUrl,
    urlKey = 'tab',
  } = props as TabsItemsProps;

  const safeItems = items ?? [];
  const defaultValue = defaultActiveKey ?? safeItems[0]?.key ?? '';

  if (syncWithUrl && controlledActiveKey === undefined) {
    return (
      <TabsUrlSyncItems
        items={safeItems}
        defaultValue={defaultValue}
        onChange={onChange}
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        className={className}
        urlKey={urlKey}
      />
    );
  }

  const listStyles = variantListStyles[variant];
  const triggerStyles = variantTriggerStyles[variant];
  const sizeClass = sizeStyles[size];

  const radixProps = controlledActiveKey !== undefined
    ? { value: controlledActiveKey, onValueChange: onChange }
    : { defaultValue, onValueChange: onChange };

  return (
    <TabsItemsRenderer
      items={safeItems}
      radixProps={radixProps}
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      className={className}
    />
  );
}

/** Renderizador interno de items-based Tabs (evita repetição). */
function TabsItemsRenderer({
  items,
  radixProps,
  variant = 'line',
  size = 'md',
  fullWidth = false,
  className,
}: {
  items: TabItem[];
  radixProps: Record<string, unknown>;
  variant?: TabVariant;
  size?: TabSize;
  fullWidth?: boolean;
  className?: string;
}) {
  const listStyles = variantListStyles[variant];
  const triggerStyles = variantTriggerStyles[variant];
  const sizeClass = sizeStyles[size];

  return (
    <RadixTabs.Root className={cn('w-full', className)} {...radixProps}>
      <RadixTabs.List
        className={cn(
          'flex',
          listStyles,
          fullWidth && 'justify-between'
        )}
      >
        {items.map((item) => (
          <RadixTabs.Trigger
            key={item.key}
            value={item.key}
            disabled={item.disabled}
            className={cn(
              'flex items-center justify-center gap-2',
              triggerStyles.base,
              triggerStyles.active,
              triggerStyles.inactive,
              sizeClass,
              fullWidth && 'flex-1 text-center'
            )}
          >
            {item.icon && <span className="h-4 w-4 shrink-0">{item.icon}</span>}
            <span>{item.label}</span>
            {item.badge !== undefined && (
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-xs',
                  variant === 'pills'
                    ? 'bg-white/20 text-white'
                    : 'bg-muted text-muted-foreground'
                )}
              >
                {item.badge}
              </span>
            )}
          </RadixTabs.Trigger>
        ))}
      </RadixTabs.List>

      {items.map((item) => (
        <RadixTabs.Content key={item.key} value={item.key} className="mt-4">
          {item.content}
        </RadixTabs.Content>
      ))}
    </RadixTabs.Root>
  );
}

/** Wrapper que sincroniza items-based Tabs com URL. */
function TabsUrlSyncItems({
  items,
  defaultValue,
  onChange,
  variant,
  size,
  fullWidth,
  className,
  urlKey,
}: {
  items: TabItem[];
  defaultValue: string;
  onChange?: (key: string) => void;
  variant?: TabVariant;
  size?: TabSize;
  fullWidth?: boolean;
  className?: string;
  urlKey: string;
}) {
  const [activeTab, setActiveTab] = useTabsUrl(urlKey, defaultValue);

  const handleChange = React.useCallback(
    (value: string) => {
      setActiveTab(value);
      onChange?.(value);
    },
    [setActiveTab, onChange],
  );

  return (
    <TabsItemsRenderer
      items={items}
      radixProps={{ value: activeTab, onValueChange: handleChange }}
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      className={className}
    />
  );
}

/** Wrapper que sincroniza compound Tabs com URL. */
function TabsUrlSync({
  urlKey,
  defaultValue,
  onChange,
  className,
  children,
}: {
  urlKey: string;
  defaultValue: string;
  onChange?: (value: string) => void;
  className?: string;
  children: React.ReactNode;
}) {
  const [activeTab, setActiveTab] = useTabsUrl(urlKey, defaultValue);

  const handleChange = React.useCallback(
    (value: string) => {
      setActiveTab(value);
      onChange?.(value);
    },
    [setActiveTab, onChange],
  );

  return (
    <RadixTabs.Root value={activeTab} onValueChange={handleChange} className={cn('w-full', className)}>
      {children}
    </RadixTabs.Root>
  );
}

// ==========================================
// TabsProvider — legado (compound com contexto)
// ==========================================

/**
 * TabsProvider — wrapper legado para compound components com contexto.
 * @deprecated Prefira usar <Tabs defaultValue> diretamente (Radix root).
 */
function TabsProvider({
  children,
  defaultActiveKey,
  onChange,
}: {
  children: React.ReactNode;
  defaultActiveKey: string;
  onChange?: (key: string) => void;
}) {
  return (
    <RadixTabs.Root defaultValue={defaultActiveKey} onValueChange={onChange} className="w-full">
      {children}
    </RadixTabs.Root>
  );
}

// ==========================================
// EXPORTS
// ==========================================

export {
  // API items-based
  Tabs,

  // Shadcn/ui compound
  TabsList,
  TabsTrigger,
  TabsContent,

  // Compound legado (compatibilidade)
  TabList,
  Tab,
  TabPanel,
  TabsProvider,

  // Radix root (para compound shadcn direto)
  TabsRoot,

  // URL sync hook (para compound mode com sync manual)
  useTabsUrl,
};
